/**
 * API client for box and item endpoints.
 *
 * All functions call the Fastify API at /api/v1/...
 * Auth token is retrieved from the Supabase session.
 */

import { supabase } from '../auth/supabase';
import { API_PREFIX } from '@shared/constants';
import type {
  Box,
  BoxWithItems,
  Item,
  MarkerRef,
  PaginatedResult,
  ListOptions,
  ApiError,
} from '@shared/types';

/** Get the current session's access token */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new ApiClientError(401, 'Unauthorized', 'No active session');
  }
  return session.access_token;
}

/** Custom error class for API responses */
export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/** Make an authenticated API request */
async function apiFetch<T>(path: string, options: globalThis.RequestInit = {}): Promise<T> {
  const token = await getAuthToken();

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let apiError: ApiError;
    try {
      apiError = await response.json();
    } catch {
      apiError = {
        statusCode: response.status,
        error: response.statusText,
        message: 'Request failed',
      };
    }
    throw new ApiClientError(apiError.statusCode, apiError.error, apiError.message);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// ── Box Endpoints ────────────────────────────────────────────────

/** Batch lookup boxes by marker refs (used by camera scanner) */
export async function batchLookup(markers: MarkerRef[]): Promise<BoxWithItems[]> {
  return apiFetch<BoxWithItems[]>('/boxes/batch-lookup', {
    method: 'POST',
    body: JSON.stringify({ markers }),
  });
}

/** Get all boxes (paginated) */
export async function listBoxes(options?: ListOptions): Promise<PaginatedResult<Box>> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('pageSize', String(options.pageSize));
  if (options?.sortBy) params.set('sortBy', options.sortBy);
  if (options?.sortOrder) params.set('sortOrder', options.sortOrder);

  const qs = params.toString();
  return apiFetch<PaginatedResult<Box>>(`/boxes${qs ? `?${qs}` : ''}`);
}

/** Get a single box with items */
export async function getBox(id: string): Promise<BoxWithItems> {
  return apiFetch<BoxWithItems>(`/boxes/${id}`);
}

/** Create a new box */
export async function createBox(data: {
  markerType: 'aruco' | 'qr';
  markerId: string;
  name: string;
  location?: string;
  groupId?: string;
  notes?: string;
}): Promise<Box> {
  return apiFetch<Box>('/boxes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Update a box */
export async function updateBox(
  id: string,
  data: {
    name?: string;
    location?: string | null;
    groupId?: string | null;
    notes?: string | null;
  },
): Promise<Box> {
  return apiFetch<Box>(`/boxes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** Delete a box */
export async function deleteBox(id: string): Promise<void> {
  return apiFetch<void>(`/boxes/${id}`, { method: 'DELETE' });
}

// ── Item Endpoints ───────────────────────────────────────────────

/** Add item to box */
export async function addItem(
  boxId: string,
  data: { name: string; quantity?: number },
): Promise<Item> {
  return apiFetch<Item>(`/boxes/${boxId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Update item */
export async function updateItem(
  id: string,
  data: { name?: string; quantity?: number },
): Promise<Item> {
  return apiFetch<Item>(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** Delete item */
export async function deleteItem(id: string): Promise<void> {
  return apiFetch<void>(`/items/${id}`, { method: 'DELETE' });
}
